import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { callTool } from "../api";
import { ContactCommunicationsPanel } from "../campaign-builder/ContactCommunicationsPanel";
import { CompanyBrowserModal, CompanyBrowserPane } from "../company-browser/CompanyBrowserModal";
import type { CompanyBrowserTarget } from "../company-browser/types";
import { StatusBlock } from "../components/StatusBlock";
import { FollowupsPane, NotesPane } from "../contacts/contactActivityPanes";
import { VerificationPane } from "../contacts/VerificationPane";
import { quickCreateAccount } from "../accounts/accountCreate";
import { QuickAccountForm } from "../accounts/QuickAccountForm";
import { LinkAccountSheet } from "../contacts/LinkAccountSheet";
import { ratingLineage } from "../ratings";
import type { LoadState } from "../types";
import { ContactAddressForm } from "./ContactAddressForm";
import { draftFromContact, emptyDraft } from "./contactDraft";
import { ContactIdentityForm } from "./ContactIdentityForm";
import { loadContactDossier, provenanceFromContact } from "./loadContactRecord";
import type { BusinessCard, ContactBrowserTarget, ContactDossier, ContactDraft, ContactOpenMode } from "./types";

type ActivityTab = "notes" | "followups" | "communications" | "verification";

interface AccountHit {
  account_id: string;
  name: string | null;
  domain: string | null;
}

function personDisplayName(draft: ContactDraft): string {
  return [draft.first_name, draft.last_name].filter(Boolean).join(" ") || draft.email || "New contact";
}

function contactPhotoSrc(value: string | null | undefined): string | null {
  const url = value?.trim();
  if (!url) return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function cardImageSrc(card: BusinessCard | null): string | null {
  if (!card) return null;
  return contactPhotoSrc(card.processed?.url) ?? contactPhotoSrc(card.original?.url);
}

function latestCards(cards: BusinessCard[] | null | undefined): { front: BusinessCard | null; back: BusinessCard | null } {
  const list = [...(cards ?? [])].sort((a, b) => String(b.captured_at ?? "").localeCompare(String(a.captured_at ?? "")));
  return {
    front: list.find((card) => card.side !== "back" && cardImageSrc(card)) ?? null,
    back: list.find((card) => card.side === "back" && cardImageSrc(card)) ?? null,
  };
}

export function ContactBrowserScreen({
  eventKey,
  campaignKey,
  target,
  actor,
  variant = "pane",
  onClose,
  onOpenCompany,
  onSaved,
  onCreated,
}: {
  eventKey: string;
  campaignKey: string;
  target: ContactBrowserTarget;
  actor: string;
  variant?: ContactOpenMode;
  onClose: () => void;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  onSaved?: () => void;
  onCreated?: (contactId: string) => void;
}) {
  const isCreate = Boolean(target.create || !target.contact_id);
  const [state, setState] = useState<LoadState<ContactDossier>>(
    isCreate ? { status: "empty", message: "" } : { status: "loading" },
  );
  const [draft, setDraft] = useState<ContactDraft>(() =>
    target.account_id ? emptyDraft(target.account_id) : emptyDraft(""),
  );
  const [editing, setEditing] = useState(isCreate);
  const [createStep, setCreateStep] = useState<"account" | "details">(target.account_id ? "details" : "account");
  const [accountPickMode, setAccountPickMode] = useState<"search" | "create">("search");
  const [accountCreateBusy, setAccountCreateBusy] = useState(false);
  const [accountCreateError, setAccountCreateError] = useState<string | null>(null);
  const [accountQuery, setAccountQuery] = useState("");
  const [accountHits, setAccountHits] = useState<AccountHit[]>([]);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [segment, setSegment] = useState("");
  const [tab, setTab] = useState<ActivityTab>("notes");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [dncPending, setDncPending] = useState<boolean | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [cardSide, setCardSide] = useState<"front" | "back">("front");

  const contactId = target.contact_id ?? null;
  const dossier = state.status === "ready" ? state.data : null;

  const load = useCallback(async () => {
    if (!contactId) return;
    setState({ status: "loading" });
    try {
      const data = await loadContactDossier(contactId, campaignKey, actor);
      setDraft(draftFromContact(data.contact, data.contact.account_id ?? ""));
      setAccountName(data.account_name);
      setSegment(data.segment_primary ?? "");
      setFavorite(data.contact.my_favorite === true);
      setDncPending(data.contact.do_not_contact === true);
      setState({ status: "ready", data });
    } catch (error) {
      setState({
        status: "error",
        message: error instanceof Error ? error.message : "contact_get failed",
      });
    }
  }, [contactId, campaignKey, actor]);

  useEffect(() => {
    if (isCreate) return;
    void load();
  }, [isCreate, load, reloadTick]);

  useEffect(() => {
    setCardOpen(false);
  }, [contactId]);

  useEffect(() => {
    if (!cardOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setCardOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [cardOpen]);

  async function searchAccounts() {
    const data = await callTool<{ results: AccountHit[] }>("account_search", {
      event_key: eventKey,
      name: accountQuery.trim() || undefined,
      limit: 20,
    });
    setAccountHits(data.results ?? []);
  }

  useEffect(() => {
    if (!isCreate || createStep !== "account" || accountPickMode !== "search") return;
    void searchAccounts().catch(() => setAccountHits([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, createStep, accountPickMode]);

  async function attachNewAccount(values: { name: string; domain: string; website: string; secondaryWebsite: string }) {
    setAccountCreateBusy(true);
    setAccountCreateError(null);
    try {
      const result = await quickCreateAccount(eventKey, actor, values);
      setDraft((prev) => ({ ...prev, account_id: result.account_id }));
      setAccountName(result.name);
      setCreateStep("details");
      setAccountPickMode("search");
    } catch (error) {
      setAccountCreateError(error instanceof Error ? error.message : "account_upsert failed");
    } finally {
      setAccountCreateBusy(false);
    }
  }

  const activityRow = useMemo(() => {
    if (!dossier) return null;
    return {
      contact_id: dossier.contact.contact_id,
      account_id: dossier.contact.account_id,
      booth: dossier.booth,
      first_name: dossier.contact.first_name,
      last_name: dossier.contact.last_name,
      company: dossier.contact.company,
    };
  }, [dossier]);

  async function patchFavorite() {
    if (!contactId) return;
    const next = !favorite;
    await callTool("contact_tag_put", {
      contact_id: contactId,
      user: actor,
      actor,
      lineage: ratingLineage(),
      favorite: next,
    });
    setFavorite(next);
  }

  function validateDraft(): string | null {
    if (!draft.account_id) return "Choose an account before saving.";
    const hasEmail = Boolean(draft.email.trim());
    const hasName = Boolean(draft.first_name.trim() && draft.last_name.trim());
    if (!hasEmail && !hasName) return "Enter an email or first and last name.";
    return null;
  }

  async function save() {
    const validation = validateDraft();
    if (validation) {
      setSaveError(validation);
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = {
        account_id: draft.account_id,
        email: draft.email.trim() || undefined,
        first_name: draft.first_name.trim() || undefined,
        last_name: draft.last_name.trim() || undefined,
        title: draft.title.trim() || undefined,
        phone: draft.phone.trim() || undefined,
        consent_basis: draft.consent_basis || undefined,
        source_url: draft.source_url.trim() || undefined,
        product_interest: draft.product_interest,
        address_line1: draft.address_line1.trim() || undefined,
        address_line2: draft.address_line2.trim() || undefined,
        address_line3: draft.address_line3.trim() || undefined,
        city: draft.city.trim() || undefined,
        state_region: draft.state_region.trim() || undefined,
        postal_code: draft.postal_code.trim() || undefined,
        country_code: draft.country_code.trim() || undefined,
        country_raw: draft.country_raw.trim() || undefined,
        actor,
        lineage: ratingLineage(),
        source: "contact-browser",
      };
      if (dncPending === true) payload.do_not_contact = true;
      else if (dncPending === false) payload.do_not_contact = false;

      const result = await callTool<{ contact_id: string }>("contact_upsert", payload);
      const savedId = result.contact_id;
      setEditing(false);
      onSaved?.();
      if (savedId && (isCreate || !contactId)) {
        onCreated?.(savedId);
      } else {
        setReloadTick((v) => v + 1);
      }
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "contact_upsert failed");
    } finally {
      setBusy(false);
    }
  }

  function cancelEdit() {
    if (isCreate) {
      onClose();
      return;
    }
    if (dossier) {
      setDraft(draftFromContact(dossier.contact, dossier.contact.account_id ?? ""));
      setDncPending(dossier.contact.do_not_contact === true);
    }
    setEditing(false);
    setSaveError(null);
  }

  const title = isCreate ? "New contact" : personDisplayName(draft);
  const Frame = variant === "pane" ? CompanyBrowserPane : CompanyBrowserModal;
  const provenance = dossier ? provenanceFromContact(dossier.contact) : [];
  const photoSrc = contactPhotoSrc(dossier?.contact.photo?.url);
  const cardPair = latestCards(dossier?.contact.business_cards);
  const cardThumb = cardImageSrc(cardPair.front) ?? cardImageSrc(cardPair.back);
  const activeCard = cardSide === "back" && cardImageSrc(cardPair.back) ? cardPair.back : cardPair.front ?? cardPair.back;
  const activeCardSrc = cardImageSrc(activeCard);
  const cardHasBoth = Boolean(cardImageSrc(cardPair.front) && cardImageSrc(cardPair.back));

  return (
    <Frame title={title} onClose={onClose} className="contact-browser-shell">
      <header className="company-modal-header">
        <div className="company-modal-heading">
          <div className={photoSrc || cardThumb ? "company-modal-identity has-photo has-media" : "company-modal-identity"}>
            {photoSrc ? (
              <img className="contact-photo" src={photoSrc} alt={`${title} photo`} referrerPolicy="no-referrer" />
            ) : null}
            {cardThumb ? (
              <button
                type="button"
                className="contact-card-thumb"
                aria-label="View business card"
                onClick={() => {
                  setCardSide(cardImageSrc(cardPair.front) ? "front" : "back");
                  setCardOpen(true);
                }}
              >
                <img src={cardThumb} alt="" referrerPolicy="no-referrer" />
              </button>
            ) : null}
            <div className="contact-identity-text">
              <h2>{title}</h2>
              <div className="company-header-meta">
                {!isCreate ? (
                  <button
                    type="button"
                    className={`star ${favorite ? "on" : ""}`}
                    aria-label={favorite ? "Unfavorite contact" : "Favorite contact"}
                    onClick={() => void patchFavorite()}
                  >
                    {favorite ? "★" : "☆"}
                  </button>
                ) : null}
                {segment ? <span className="filter-pill segment-pill">{segment}</span> : null}
                {dossier?.contact.event_contact_display ? (
                  <span className="filter-pill">{dossier.contact.event_contact_display}</span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="company-modal-actions">
            {editing ? (
              <>
                <button type="button" className="secondary" disabled={busy} onClick={cancelEdit}>
                  Cancel
                </button>
                <button type="button" disabled={busy} onClick={() => void save()}>
                  {busy ? "Saving…" : "Save"}
                </button>
              </>
            ) : (
              <button type="button" className="secondary" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
            <button type="button" className="secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        {dncPending ? (
          <p className="contact-dnc-banner" role="status">
            Unsubscribed — do not contact. Marketing sends are suppressed.
          </p>
        ) : null}
        {saveError ? <p className="err">{saveError}</p> : null}
      </header>

      <div className="company-modal-body contact-browser-body">
        {isCreate && createStep === "account" ? (
          <section className="contact-section">
            <h3>Account</h3>
            <p className="muted">A contact must be attached to an account.</p>
            <div className="account-pick-mode" role="tablist" aria-label="Account selection">
              <button
                type="button"
                role="tab"
                aria-selected={accountPickMode === "search"}
                className={accountPickMode === "search" ? "on" : ""}
                onClick={() => setAccountPickMode("search")}
              >
                Search existing
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={accountPickMode === "create"}
                className={accountPickMode === "create" ? "on" : ""}
                onClick={() => setAccountPickMode("create")}
              >
                Create new
              </button>
            </div>
            {accountPickMode === "search" ? (
              <>
                <form
                  className="toolbar"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void searchAccounts();
                  }}
                >
                  <input
                    value={accountQuery}
                    onChange={(e) => setAccountQuery(e.target.value)}
                    placeholder="Account name"
                  />
                  <button type="submit" className="secondary">Search</button>
                </form>
                <ul className="account-pick-list">
                  {accountHits.map((hit) => (
                    <li key={hit.account_id}>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => {
                          setDraft((prev) => ({ ...prev, account_id: hit.account_id }));
                          setAccountName(hit.name);
                          setCreateStep("details");
                        }}
                      >
                        {hit.name ?? hit.account_id}
                        {hit.domain ? ` · ${hit.domain}` : ""}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <QuickAccountForm
                busy={accountCreateBusy}
                error={accountCreateError}
                submitLabel="Use this account"
                onSubmit={(values) => void attachNewAccount(values)}
              />
            )}
          </section>
        ) : null}

        {isCreate && createStep === "details" ? (
          <p className="muted">
            Account: <strong>{accountName ?? draft.account_id}</strong>
            <button type="button" className="ghost" onClick={() => setCreateStep("account")}>
              Change
            </button>
          </p>
        ) : null}

        {!isCreate && state.status !== "ready" ? (
          <StatusBlock state={state} loadingLabel="Loading contact…" />
        ) : null}

        {(isCreate && createStep === "details") || (!isCreate && dossier) ? (
          <>
            <section className="contact-section">
              <h3>Account</h3>
              {draft.account_id ? (
                <div className="contact-account-row">
                  <button
                    type="button"
                    className="ghost"
                    onClick={() =>
                      onOpenCompany?.({ account_id: draft.account_id, tab: "people" })
                    }
                  >
                    {accountName ?? dossier?.account_name ?? "Open account"}
                  </button>
                  {!isCreate ? (
                    <button type="button" className="ghost" onClick={() => setLinkOpen(true)}>
                      Change account
                    </button>
                  ) : null}
                </div>
              ) : (
                <button type="button" className="ghost" onClick={() => setLinkOpen(true)}>
                  Link account
                </button>
              )}
            </section>

            <section className="contact-section">
              <h3>Identity</h3>
              <ContactIdentityForm
                draft={draft}
                editing={editing}
                onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
              />
            </section>

            <section className="contact-section">
              <h3>Address</h3>
              <ContactAddressForm
                draft={draft}
                editing={editing}
                onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
              />
            </section>

            <section className="contact-section">
              <h3>Do not contact (unsubscribed)</h3>
              {editing ? (
                <label className="field checkbox-field">
                  <input
                    type="checkbox"
                    checked={dncPending === true}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      if (checked) {
                        if (window.confirm("Mark this person as unsubscribed / do not contact?")) {
                          setDncPending(true);
                        }
                      } else if (window.confirm("Allow contact again? This clears the do-not-contact flag.")) {
                        setDncPending(false);
                      }
                    }}
                  />
                  Enabled
                </label>
              ) : (
                <p>{dncPending ? "Yes — do not contact" : "No"}</p>
              )}
            </section>

            {provenance.length ? (
              <section className="contact-section">
                <h3>Provenance</h3>
                <ul className="company-list">
                  {provenance.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!isCreate && activityRow ? (
              <>
                <div className="company-tabs" role="tablist" aria-label="Contact activity">
                  <button
                    type="button"
                    role="tab"
                    className={tab === "notes" ? "tab on" : "tab"}
                    aria-selected={tab === "notes"}
                    onClick={() => setTab("notes")}
                  >
                    Notes
                  </button>
                  <button
                    type="button"
                    role="tab"
                    className={tab === "followups" ? "tab on" : "tab"}
                    aria-selected={tab === "followups"}
                    onClick={() => setTab("followups")}
                  >
                    Follow-ups
                  </button>
                  <button
                    type="button"
                    role="tab"
                    className={tab === "communications" ? "tab on" : "tab"}
                    aria-selected={tab === "communications"}
                    onClick={() => setTab("communications")}
                  >
                    Campaign mail
                  </button>
                  <button
                    type="button"
                    role="tab"
                    className={tab === "verification" ? "tab on" : "tab"}
                    aria-selected={tab === "verification"}
                    onClick={() => setTab("verification")}
                  >
                    Verification
                  </button>
                </div>
                {tab === "notes" ? <NotesPane row={activityRow} actor={actor} /> : null}
                {tab === "followups" ? (
                  <FollowupsPane row={activityRow} eventKey={eventKey} actor={actor} />
                ) : null}
                {tab === "communications" ? (
                  <ContactCommunicationsPanel contactId={activityRow.contact_id} />
                ) : null}
                {tab === "verification" ? (
                  <VerificationPane contactId={activityRow.contact_id} email={dossier?.contact.email ?? ""} />
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>

      {linkOpen && contactId && activityRow ? (
        <LinkAccountSheet
          row={activityRow}
          eventKey={eventKey}
          actor={actor}
          onClose={() => setLinkOpen(false)}
          onSaved={() => {
            setLinkOpen(false);
            setReloadTick((v) => v + 1);
            onSaved?.();
          }}
        />
      ) : null}
      {cardOpen && activeCardSrc
        ? createPortal(
            <div className="contact-card-lightbox" role="dialog" aria-modal="true" aria-label="Business card">
              <button type="button" className="contact-card-lightbox-backdrop" aria-label="Close business card" onClick={() => setCardOpen(false)} />
              <div className="contact-card-lightbox-panel">
                {cardHasBoth ? (
                  <div className="contact-card-sides" role="tablist" aria-label="Card side">
                    <button type="button" role="tab" aria-selected={cardSide !== "back"} onClick={() => setCardSide("front")}>
                      Front
                    </button>
                    <button type="button" role="tab" aria-selected={cardSide === "back"} onClick={() => setCardSide("back")}>
                      Back
                    </button>
                  </div>
                ) : null}
                <img src={activeCardSrc} alt={`${title} business card`} referrerPolicy="no-referrer" />
                <button type="button" className="secondary" onClick={() => setCardOpen(false)}>
                  Close
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </Frame>
  );
}
